import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/utilities/ProtectedRoute";
import { Suspense, useEffect, useRef } from "react";
import { useAuth } from "./contexts/AuthContext";
import ProjectsSection from "./pages/projects/ProjectsSection";
import BillingSection from "./components/dashboard/BillingSection";
import SettingsSection from "./components/dashboard/SettingsSection";
import IntegrationsSection from "./components/dashboard/IntegrationsSection";
import NotionSuccess from "./pages/integrations/NotionSuccess";
import NotionError from "./pages/integrations/NotionError";
import GithubSetupComplete from "./pages/auth/GithubSetupComplete";
import { Navbar } from "./components/utilities/Navbar";
import { Footer } from "./components/utilities/Footer";
import Sidebar from "./components/dashboard/Sidebar";
import Dashboard from "./components/utilities/Dashboard";
import NotFound from "./components/utilities/NotFound";
import EmailVerification from "./pages/auth/EmailVerification";
import ForgotPassword from "./pages/auth/ForgotPassword";
import GitHubCallback from "./pages/auth/GitHubCallback";
import GoogleCallback from "./pages/auth/GoogleCallback";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ResetPassword from "./pages/auth/ResetPassword";
import Index from "./pages/Index";
import ProjectPage from "./pages/projects/ProjectPage";
import RepoPage from "./pages/repos/RepoPage";
import DocsPage from "./pages/docs/Index";
import AboutUs from "./pages/about/AboutUs";
import ContactUs from "./pages/contact/co-founderpage";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfUse from "./pages/legal/TermsOfUse";
import PricingPage from "./pages/pricing/PricingPage";
import BlogPage from "./pages/blog/BlogPage";
import { useToast } from "@/components/ui/use-toast";

// Optimized loading animation with faster animation and simplified structure
export const LoadingAnimation = () => {
  return (
    <div className="fixed inset-0 flex justify-center items-center bg-white/30 dark:bg-black/30 backdrop-blur-sm z-50">
      <div className="h-16 w-16 rounded-full border-4 border-t-[#00ff9d] border-[#00ff9d]/30 animate-spin"></div>
    </div>
  );
};

// Component to handle restricted routes
const RestrictedRoute = ({ children }) => {
  const { toast } = useToast();
  const location = useLocation();
  
  // If user is trying to access the login page, allow it
  // This is to let whitelisted users access the login page directly
  if (location.pathname === "/auth/login") {
    return children;
  }
  
  // Show a toast message about the restricted access
  toast({
    title: "Private Beta",
    description: "This application is in private beta. Please book a demo or contact us for access.",
    duration: 5000,
  });
  
  // Redirect to home page
  return <Navigate to="/" replace />;
};

export const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const prevPathRef = useRef(location.pathname);

  // Define routes where sidebar should NOT appear
  const noSidebarRoutes = ["/", "/auth/login", "/auth/register", "/docs", "/about", "/contact", "/privacy-policy", "/terms-of-use", "/pricing", "/blog"];

  // Enhanced scroll behavior that ensures scrolling works consistently
  useEffect(() => {
    // Only scroll if the path actually changed (not just a hash/search change)
    if (prevPathRef.current !== location.pathname) {
      // Use multiple scroll methods for maximum browser compatibility
      window.scrollTo(0, 0);
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      
      prevPathRef.current = location.pathname;
    }
  }, [location]);

  if (user == null && localStorage.getItem("token")) {
    return <LoadingAnimation />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex flex-1">
        {!noSidebarRoutes.includes(location.pathname) && <Sidebar />}
        <main className={`flex-1 ${
          location.pathname === "/" || location.pathname === "/about" 
            ? "" 
            : noSidebarRoutes.includes(location.pathname)
              ? "flex flex-col min-h-screen mt-10"
              : "flex flex-col min-h-screen bg-white dark:bg-[#0d1117]"
        }`}>
          <Suspense fallback={<LoadingAnimation />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/github/setup-error/*"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/auth/login" element={<Login />} />
              <Route path="/github/setup-complete" element={<GithubSetupComplete />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/projects" element={<ProjectsSection />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/settings" element={<SettingsSection />} />
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute>
                    <IntegrationsSection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/notion/success"
                element={
                  <ProtectedRoute>
                    <NotionSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/notion/error"
                element={
                  <ProtectedRoute>
                    <NotionError />
                  </ProtectedRoute>
                }
              />
              <Route path="/auth/register" element={<Register />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/verify" element={<EmailVerification />} />
              <Route path="/auth/callback/google" element={<GoogleCallback />} />
              <Route path="/auth/callback/github" element={<GitHubCallback />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/project/:id" element={<ProjectPage />} />
              <Route path="/repo/:id" element={<RepoPage />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
};